import { uniq } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import replace from 'react-string-replace';
import { isNotificationUnread } from '../../../data/filters/notifications/isNotificationUnread';
import { useChatWithUserPathname } from '../../../data/hooks/chat/useChatWithUserPathname';
import { useEvent } from '../../../data/hooks/eventHooks';
import { useEventSettings } from '../../../data/hooks/events/useEventSettings';
import { useI18n } from '../../../data/hooks/i18nHooks';
import { useEventQAAnswerNotifications } from '../../../data/hooks/notifications/useEventQAAnswerNotifications';
import { useEventQuestion } from '../../../data/hooks/questions/useEventQuestion';
import { useIsQAActive } from '../../../data/hooks/questions/useIsQAActive';
import { useQuestionAnswers } from '../../../data/hooks/questions/useQuestionAnswers';
import { useMyUserId } from '../../../data/hooks/sessionHooks';
import Button from '../../elements/Button/Button';
import FooterButtonsLayout from '../../elements/FooterButtonsLayout';
import Icon from '../../elements/icons/Icon';
import Link from '../../elements/Link';
import Scrollable from '../../elements/Scrollable';
import TopBarEvent from '../../elements/TopBar/TopBarEvent';
import ErrorBoundary from '../../validate/ErrorBoundary';
import WaitWhileBusy from '../../WaitWhileBusy';
import Screen from './../Screen';
import Answer from './common/Answer/Answer';
import FavoriteCount from './common/FavoriteCount';
import FavoriteLink from './common/FavoriteLink/FavoriteLink';
import EventQuestQuestionListItem from './EventQuestQuestionListItem/EventQuestQuestionListItem';

/**
 * Screen for a single event question: the question, a toolbar (favorite, share, edit, chat)
 * and its answers, with footer buttons to reply or invite the involved users to a group.
 * Marks the question's unread answer notifications as read.
 *
 * @param {Object} props
 * @param {string|number} props.eventId - Event the question belongs to.
 * @param {string|number} props.questionId - Question to show.
 * @returns {JSX.Element}
 */
const EventQuestsQuestion = ({ eventId, questionId }) => {
    let i18n = useI18n(),
        event = useEvent(eventId),
        question = useEventQuestion(eventId, questionId),
        answers = useQuestionAnswers(eventId, questionId),
        notifications = useEventQAAnswerNotifications(eventId, questionId, { filter: isNotificationUnread }),
        isQAOpen = useIsQAActive(eventId),
        myUserId = useMyUserId(),
        isMine = question.asker_id == myUserId,
        involvedUsers = uniq([question.asker_id, ...answers.map((answer) => answer.answerer.id)]),
        { twitter_disabled } = useEventSettings(eventId),
        hasSelfAnswer = involvedUsers.find((id) => id == myUserId),
        chatHref = useChatWithUserPathname(question.asker.id);

    // Mark notifications as read
    useEffect(() => {
        notifications.forEach((notif) => app.api.notifications.markRead(notif.id));
    }, [notifications]);

    // const handleQuestionImageClicked = null
    const handleQuestionImageClicked = useCallback((attachment) => {
        app.history.push(['.', 'attached', attachment.id.toString()].join('/'));
    }, []);

    const handleAskerAvatarClicked = useCallback(() => {
        let asker = question.asker;
        if (!question.anonymous && asker) {
            app.history.push(['.', 'participants', asker.id, '-'].join('/'));
        }
    }, [question]);

    const toolbarOptions = [
        !isMine ? <FavoriteLink event={event} question={question} /> : null,
        <FavoriteCount question={question} />,
        isMine && !twitter_disabled ? (
            <Link href={'https://twitter.com/share?text=&url=' + encodeURIComponent(location.href) + '&hashtags=networkapp'}>
                {replace(i18n['label.share.via.twitter'], '{0}', (match, i) => (
                    <Icon key={i} size={16} src="/img/twitter_white_small.png" />
                ))}
            </Link>
        ) : null,
        isMine ? <Link href="./edit">{i18n['label.edit']}</Link> : null,
        !isMine && hasSelfAnswer ? <Link href={chatHref}>{i18n['label.chat']}</Link> : null,
    ].filter(Boolean);

    const toolbar = (
        <table className="toolbar" style={event._local.styles.button}>
            <tbody>
                <tr>
                    {toolbarOptions.map((option, i) => (
                        <td style={{ width: (100 / toolbarOptions.length).toString() + '%' }} key={i}>
                            {option}
                        </td>
                    ))}
                </tr>
            </tbody>
        </table>
    );

    const header = (
        <div className="header">
            <TopBarEvent returnURL="../.." event={event} qa="qa">
                {i18n['label.questions']}
            </TopBarEvent>
        </div>
    );

    const footer = (
        <>
            <FooterButtonsLayout className="footer">
                {involvedUsers.length > 2 && hasSelfAnswer ? (
                    <Button className="flex-shrink-2 flex-grow-0" href={`./invite?users=${involvedUsers.filter((id) => id != myUserId)}`} primary outline>
                        {i18n['button.group_invite']}
                    </Button>
                ) : null}
                {isQAOpen ? (
                    <Button className="flex-grow-2" href="./create" primary>
                        {i18n['label.post.reply']}
                    </Button>
                ) : null}
            </FooterButtonsLayout>
        </>
    );

    // NOTE: don't show question on header because if the content is too long it will leave the answers area unusable

    return (
        <Screen header={header} footer={footer}>
            <Scrollable className="" savePosition={'questionAnswers-' + question.id}>
                <EventQuestQuestionListItem event={event} question={question} href={null} showTagsOnList trimQuestionContent={false} onImageClicked={handleQuestionImageClicked} />
                {toolbar}
                <hr />
                <div className="">
                    {answers.map((answer) => (
                        <Answer key={answer.id} event={event} question={question} answer={answer} />
                    ))}
                </div>
            </Scrollable>
        </Screen>
    );
};

/**
 * Route entry point: wraps the screen in an error boundary and a busy-wait, taking
 * `eventId` and `questionId` from the route params.
 *
 * @param {Object} props
 * @param {{params: {eventId: string, questionId: string}}} props.match - React Router match.
 * @returns {JSX.Element}
 */
export default (props) => (
    <ErrorBoundary altRoute="..">
        <WaitWhileBusy>
            <EventQuestsQuestion {...props.match.params} />
        </WaitWhileBusy>
    </ErrorBoundary>
);
